import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import axios from 'axios';
import { apiUrl } from '../../../environment/index';
import ns811Logo from '../../../assets/images/ns-811-logo.png';
import florizelLogo from '../../../assets/images/florizel-powered-by-fonemed.png';
import nscafAddresses from './data/nscaf-addresses.json';
import './NFPRPublicIntake.css';

const NOVA_SCOTIA_HEALTH_LOGO_URL = 'https://www.nshealth.ca/themes/custom/nsh/build/assets/images/logo-footer.png';

const initialForm = {
  livesInNovaScotia: '',
  hasProvider: '',
  hasHealthCard: '',
  registeringFor: 'self',
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  healthCardNumber: '',
  phone: '',
  email: '',
  addressLine1: '',
  community: '',
  postalCode: '',
  preferredLanguage: 'English',
  preferredContactMethod: 'Phone',
  familyMemberCount: '0',
  medicationNeeds: false,
  chronicCondition: false,
  pregnant: false,
  mobilityNeeds: false,
  mentalHealthSupport: false,
  transportationBarrier: false,
  virtualCareSupport: false,
  additionalNotes: '',
  consentAccuracy: false,
  consentContact: false,
  consentEmergency: false,
  confirmLastName: '',
  confirmDateOfBirth: '',
  confirmHealthCardNumber: '',
  removeLastName: '',
  removeDateOfBirth: '',
  removeHealthCardNumber: '',
  removeConsent: false,
};

const registerSteps = [
  'Check eligibility',
  'Applicant details',
  'Health needs',
  'Review and consent',
];

const contactMethods = ['Phone', 'Email', 'Either phone or email'];
const languages = ['English', 'French', 'Other'];

const localAddressSuggestions = nscafAddresses.map((address) => ({
  ...address,
  source: address.source || 'nscaf-local',
}));

const normalizeAddressSearch = (value = '') => String(value)
  .toLowerCase()
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const searchLocalNSCAFAddresses = (searchTerm, limit = 8) => {
  const query = normalizeAddressSearch(searchTerm);

  if (!query || query.length < 2) {
    return localAddressSuggestions.slice(0, limit);
  }

  const terms = query.split(' ').filter(Boolean);

  return localAddressSuggestions
    .map((address) => {
      const haystack = normalizeAddressSearch([
        address.label,
        address.addressLine1,
        address.community,
        address.county,
        address.postalCode,
      ].filter(Boolean).join(' '));

      const allTermsMatch = terms.every((term) => haystack.includes(term));
      const startsWithScore = haystack.startsWith(query) ? 20 : 0;
      const labelIncludesScore = haystack.includes(query) ? 10 : 0;
      const termScore = terms.reduce((score, term) => score + (haystack.includes(term) ? 1 : 0), 0);

      return {
        address,
        match: allTermsMatch || haystack.includes(query),
        score: startsWithScore + labelIncludesScore + termScore,
      };
    })
    .filter((item) => item.match)
    .sort((a, b) => b.score - a.score || a.address.label.localeCompare(b.address.label))
    .slice(0, limit)
    .map((item) => item.address);
};

const createConfirmationNumber = (prefix = 'NFPR') => {
  const now = new Date();
  const year = now.getFullYear();
  const suffix = Math.floor(100000 + Math.random() * 900000);
  return `${prefix}-${year}-${suffix}`;
};

const REGISTRY_STATUS_LABELS = {
  arrived: 'Submitted',
  'needs-review': 'Needs review',
  'in-progress': 'In progress',
  'follow-up': 'Follow-up required',
  validated: 'Validated',
  'patient-linked': 'Patient linked',
  closed: 'Closed',
};

const getRegistryStatusLabel = (status) => REGISTRY_STATUS_LABELS[status] || status || 'Unknown';

const normalizeBool = (value) => value === true || value === 'yes';

const healthCardFields = new Set([
  'healthCardNumber',
  'confirmHealthCardNumber',
  'removeHealthCardNumber',
]);

const getHealthCardDigits = (value = '') => String(value).replace(/\D/g, '');

const formatNSHealthCardNumber = (value = '') => {
  const digits = getHealthCardDigits(value).slice(0, 10);
  const parts = [digits.slice(0, 4), digits.slice(4, 7), digits.slice(7, 10)].filter(Boolean);
  return parts.join(' ');
};

const isValidNSHealthCardNumber = (value = '') => getHealthCardDigits(value).length === 10;


const ADDRESS_COMPLETE_KEY = process.env.REACT_APP_ADDRESS_COMPLETE_KEY || 'hj47-de61-mg39-ry39';
const ADDRESS_COMPLETE_COUNTRY = process.env.REACT_APP_ADDRESS_COMPLETE_COUNTRY || 'CAN';
const ADDRESS_COMPLETE_LANGUAGE = process.env.REACT_APP_ADDRESS_COMPLETE_LANGUAGE || 'EN';
const ADDRESS_COMPLETE_MAX_SUGGESTIONS = Number(process.env.REACT_APP_ADDRESS_COMPLETE_MAX_SUGGESTIONS || 7);
const ADDRESS_COMPLETE_CSS_ID = 'canada-post-addresscomplete-css';
const ADDRESS_COMPLETE_SCRIPT_ID = 'canada-post-addresscomplete-js';
const ADDRESS_COMPLETE_CSS_URL = `https://ws1.postescanada-canadapost.ca/css/addresscomplete-2.50.min.css?key=${ADDRESS_COMPLETE_KEY}`;
const ADDRESS_COMPLETE_SCRIPT_URL = `https://ws1.postescanada-canadapost.ca/js/addresscomplete-2.50.min.js?key=${ADDRESS_COMPLETE_KEY}`;
const ADDRESS_COMPLETE_FIND_URL = 'https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Find/v2.10/json3.ws';
const ADDRESS_COMPLETE_RETRIEVE_URL = 'https://ws1.postescanada-canadapost.ca/AddressComplete/Interactive/Retrieve/v2.11/json3.ws';

const normalizeAddressCompleteResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.Items)) {
    return response.Items;
  }

  if (Array.isArray(response?.items)) {
    return response.items;
  }

  return [];
};

const jsonpRequest = (url) => new Promise((resolve, reject) => {
  const callbackName = `addressCompleteCallback_${Date.now()}_${Math.floor(Math.random() * 100000)}`;
  const separator = url.includes('?') ? '&' : '?';
  const script = document.createElement('script');
  const cleanup = () => {
    delete window[callbackName];
    if (script.parentNode) {
      script.parentNode.removeChild(script);
    }
  };

  const timeout = window.setTimeout(() => {
    cleanup();
    reject(new Error('Address lookup timed out.'));
  }, 8000);

  window[callbackName] = (data) => {
    window.clearTimeout(timeout);
    cleanup();
    resolve(data);
  };

  script.src = `${url}${separator}callback=${callbackName}`;
  script.async = true;
  script.onerror = () => {
    window.clearTimeout(timeout);
    cleanup();
    reject(new Error('Address lookup is unavailable.'));
  };

  document.body.appendChild(script);
});

const buildQueryString = (params) => new URLSearchParams(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
).toString();

const loadExternalStyle = (id, href) => {
  if (!href || document.getElementById(id)) {
    return;
  }

  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.type = 'text/css';
  link.href = href;
  document.head.appendChild(link);
};

const loadExternalScript = (id, src) => new Promise((resolve, reject) => {
  if (!src) {
    reject(new Error('AddressComplete script URL is not configured.'));
    return;
  }

  const existingScript = document.getElementById(id);
  if (existingScript) {
    if (window.pca) {
      resolve(window.pca);
      return;
    }

    existingScript.addEventListener('load', () => resolve(window.pca), { once: true });
    existingScript.addEventListener('error', () => reject(new Error('AddressComplete failed to load.')), { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = id;
  script.type = 'text/javascript';
  script.src = src;
  script.async = true;
  script.onload = () => resolve(window.pca);
  script.onerror = () => reject(new Error('AddressComplete failed to load.'));
  document.body.appendChild(script);
});

const findAddressCompleteSuggestions = async (searchTerm, lastId = '') => {
  if (!ADDRESS_COMPLETE_KEY || !searchTerm.trim()) {
    return [];
  }

  const query = buildQueryString({
    Key: ADDRESS_COMPLETE_KEY,
    SearchTerm: searchTerm.trim(),
    LastId: lastId,
    Country: ADDRESS_COMPLETE_COUNTRY,
    LanguagePreference: ADDRESS_COMPLETE_LANGUAGE,
    MaxSuggestions: ADDRESS_COMPLETE_MAX_SUGGESTIONS,
  });

  const response = await jsonpRequest(`${ADDRESS_COMPLETE_FIND_URL}?${query}`);
  return normalizeAddressCompleteResponse(response)
    .filter((item) => !item.Error)
    .map((item) => ({
      source: 'canada-post-addresscomplete',
      id: item.Id,
      label: [item.Text, item.Description].filter(Boolean).join(', '),
      text: item.Text,
      description: item.Description,
      next: item.Next,
    }));
};

const retrieveAddressCompleteAddress = async (id) => {
  if (!ADDRESS_COMPLETE_KEY || !id) {
    return null;
  }

  const query = buildQueryString({
    Key: ADDRESS_COMPLETE_KEY,
    Id: id,
  });

  const response = await jsonpRequest(`${ADDRESS_COMPLETE_RETRIEVE_URL}?${query}`);
  const [address] = normalizeAddressCompleteResponse(response).filter((item) => !item.Error);

  if (!address) {
    return null;
  }

  const addressLine1 = [address.SubBuilding, address.BuildingNumber, address.Street]
    .filter(Boolean)
    .join(' ') || address.Line1 || address.Label || '';

  return {
    source: 'canada-post-addresscomplete',
    label: address.Label || [address.Line1, address.City, address.ProvinceCode || address.Province, address.PostalCode].filter(Boolean).join(', '),
    addressLine1,
    community: address.City || address.District || '',
    postalCode: address.PostalCode || '',
    provinceCode: address.ProvinceCode || address.Province || '',
    raw: address,
  };
};


const NSCAF_OPEN_DATA_URL = 'https://data.novascotia.ca/resource/tntn-er5g.json';
const NSCAF_OPEN_DATA_ENABLED = process.env.REACT_APP_NFPR_ADDRESS_SOURCE !== 'local';
const NSCAF_OPEN_DATA_MAX_SUGGESTIONS = Number(process.env.REACT_APP_NFPR_NSCAF_MAX_SUGGESTIONS || 8);

const firstValue = (record, keys) => {
  for (const key of keys) {
    if (record && record[key] !== undefined && record[key] !== null && String(record[key]).trim() !== '') {
      return String(record[key]).trim();
    }
  }
  return '';
};

const normalizeNSCAFOpenDataRecord = (record = {}) => {
  const civicNumber = firstValue(record, ['civicnum', 'civic_num', 'civicnumber', 'civnum']);
  const civicSuffix = firstValue(record, ['civsuffix', 'civ_suffix', 'civalpha']);
  const unitNumber = firstValue(record, ['unit_num', 'unitnum', 'apartment']);
  const street = firstValue(record, ['street', 'street_1', 'streetname', 'str_name']);
  const community = firstValue(record, ['comm', 'city', 'city_1', 'gsa_name', 'community']);
  const county = firstValue(record, ['county', 'co_code']);
  const municipality = firstValue(record, ['mun', 'municipality', 'mun_code']);
  const postalCode = firstValue(record, ['postal_cod', 'postal', 'postalcode', 'post_code']);
  const latitude = firstValue(record, ['lat', 'latitude']);
  const longitude = firstValue(record, ['long', 'longitude', 'lon']);
  const id = firstValue(record, ['pntid', 'pntid_1', 'objectid', ':id']) || `${civicNumber}-${street}-${community}`;

  const addressLine1 = [unitNumber && `Unit ${unitNumber}`, [civicNumber, civicSuffix].filter(Boolean).join(''), street]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  const label = [addressLine1, community, postalCode]
    .filter(Boolean)
    .join(', ');

  return {
    source: 'nscaf-open-data',
    id,
    label,
    addressLine1,
    community,
    county,
    municipality,
    postalCode,
    latitude,
    longitude,
    raw: record,
  };
};

const findNSCAFOpenDataAddresses = async (searchTerm, limit = NSCAF_OPEN_DATA_MAX_SUGGESTIONS) => {
  const query = searchTerm.trim();

  if (!query || query.length < 3 || !NSCAF_OPEN_DATA_ENABLED) {
    return [];
  }

  const params = new URLSearchParams({
    '$limit': String(Math.min(Math.max(limit * 3, limit), 50)),
    '$q': query,
  });

  const response = await fetch(`${NSCAF_OPEN_DATA_URL}?${params.toString()}`);

  if (!response.ok) {
    throw new Error('NSCAF address lookup failed.');
  }

  const records = await response.json();
  const normalizedQuery = normalizeAddressSearch(query);
  const queryTerms = normalizedQuery.split(' ').filter(Boolean);

  return records
    .map(normalizeNSCAFOpenDataRecord)
    .filter((address) => address.addressLine1 && address.community)
    .map((address) => {
      const haystack = normalizeAddressSearch([
        address.label,
        address.addressLine1,
        address.community,
        address.county,
        address.municipality,
        address.postalCode,
      ].filter(Boolean).join(' '));
      const allTermsMatch = queryTerms.every((term) => haystack.includes(term));
      const score = (haystack.startsWith(normalizedQuery) ? 20 : 0)
        + (haystack.includes(normalizedQuery) ? 10 : 0)
        + queryTerms.reduce((total, term) => total + (haystack.includes(term) ? 1 : 0), 0);
      return { address, match: allTermsMatch || haystack.includes(normalizedQuery), score };
    })
    .filter((item) => item.match)
    .sort((a, b) => b.score - a.score || a.address.label.localeCompare(b.address.label))
    .slice(0, limit)
    .map((item) => item.address);
};

const getHealthCardHelperText = (value = '') => {
  if (!value) {
    return 'Enter the 10-digit number on your Nova Scotia Health Card.';
  }

  return isValidNSHealthCardNumber(value)
    ? 'Format looks correct.'
    : 'Enter a 10-digit Nova Scotia Health Card number. Example: 1234 567 897.';
};

const buildQueuePayload = (form, confirmationNumber, requestType) => {
  const flags = [
    form.medicationNeeds && 'Ongoing medication needs',
    form.chronicCondition && 'Chronic condition',
    form.pregnant && 'Pregnancy',
    form.mobilityNeeds && 'Mobility/accessibility need',
    form.mentalHealthSupport && 'Mental health/addictions support',
    form.transportationBarrier && 'Transportation barrier',
    form.virtualCareSupport && 'Virtual care support need',
  ].filter(Boolean);

  return {
    status: 'arrived',
    priority: 'routine',
    source: 'Public Web Intake',
    queueType: 'Need a Family Practice Registry',
    reasonForCall: requestType,
    scopeOfPractice: 'Registry Support / 811 Navigation',
    organization: 'Nova Scotia 811',
    patient: {
      firstName: form.firstName,
      lastName: form.lastName,
      dateOfBirth: form.dateOfBirth,
      healthCardNumber: form.healthCardNumber,
      phone: form.phone,
      email: form.email,
      addressLine1: form.addressLine1,
      community: form.community,
      postalCode: form.postalCode,
      preferredLanguage: form.preferredLanguage,
      preferredContactMethod: form.preferredContactMethod,
    },
    metadata: {
      confirmationNumber,
      hasNSHealthCard: normalizeBool(form.hasHealthCard),
      registeringFor: form.registeringFor,
      familyMemberCount: Number(form.familyMemberCount || 0),
      consentCaptured: Boolean(form.consentAccuracy && form.consentContact && form.consentEmergency),
      healthNeedsFlags: flags,
      additionalNotes: form.additionalNotes,
      submittedAt: new Date().toISOString(),
    },
    note: `Need a Family Practice request submitted. Preferred contact: ${form.preferredContactMethod}. Health needs: ${flags.join(', ') || 'None reported'}.`,
  };
};

const Field = ({ label, value }) => (
  <Box className="nfpr-summary-field">
    <Typography variant="caption">{label}</Typography>
    <Typography variant="body1">{value || 'Not provided'}</Typography>
  </Box>
);

const LogoHeader = ({ centered = false }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={2}
    alignItems={centered ? 'center' : { xs: 'flex-start', sm: 'center' }}
    justifyContent={centered ? 'center' : 'flex-start'}
    className={`nfpr-brand-row ${centered ? 'nfpr-brand-row-centered' : ''}`}
  >
    <Box className="nfpr-health-logo-panel">
      <img src={NOVA_SCOTIA_HEALTH_LOGO_URL} alt="Nova Scotia Health" className="nfpr-logo nfpr-logo-health" />
    </Box>
    <Box className="nfpr-811-logo-panel">
      <img src={ns811Logo} alt="811 Peace of mind 24/7" className="nfpr-logo nfpr-logo-811" />
    </Box>
  </Stack>
);

const PoweredByFooter = () => (
  <Box className="nfpr-powered-footer" aria-label="Powered by Florizel">
    <Typography variant="caption" className="nfpr-powered-label">Powered by</Typography>
    <img src={florizelLogo} alt="Florizel powered by Fonemed" className="nfpr-powered-logo" />
  </Box>
);

const NFPRPublicIntake = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [serviceAction, setServiceAction] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedAction, setSubmittedAction] = useState('');
  const [confirmResult, setConfirmResult] = useState(null);
  const [confirmationNumber, setConfirmationNumber] = useState('');
  const [error, setError] = useState('');
  const [addressInput, setAddressInput] = useState('');
  const [addressOptions, setAddressOptions] = useState(localAddressSuggestions.slice(0, 8));
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState('');
  const addressCompleteControlRef = useRef(null);

  useEffect(() => {
    if (activeStep !== 1 || !ADDRESS_COMPLETE_KEY || addressCompleteControlRef.current) {
      return undefined;
    }

    let cancelled = false;

    const initializeAddressComplete = async () => {
      try {
        loadExternalStyle(ADDRESS_COMPLETE_CSS_ID, ADDRESS_COMPLETE_CSS_URL);
        const pca = await loadExternalScript(ADDRESS_COMPLETE_SCRIPT_ID, ADDRESS_COMPLETE_SCRIPT_URL);

        if (cancelled || !pca || !document.getElementById('nfpr-address-search')) {
          return;
        }

        const fields = [
          { element: 'nfpr-address-search', field: '', mode: pca.fieldMode.SEARCH },
        ];

        const options = {
          key: ADDRESS_COMPLETE_KEY,
          countries: { codesList: ADDRESS_COMPLETE_COUNTRY },
          languagePreference: ADDRESS_COMPLETE_LANGUAGE,
          maxSuggestions: ADDRESS_COMPLETE_MAX_SUGGESTIONS,
          bar: { visible: false },
        };

        const control = new pca.Address(fields, options);
        addressCompleteControlRef.current = control;

        control.listen('populate', (address) => {
          const provinceCode = String(address.ProvinceCode || address.Province || '').toUpperCase();

          if (provinceCode && !['NS', 'NOVA SCOTIA'].includes(provinceCode)) {
            setAddressLookupError('Please select a Nova Scotia address or enter your Nova Scotia address manually.');
            return;
          }

          const addressLine1 = [address.SubBuilding, address.BuildingNumber, address.Street]
            .filter(Boolean)
            .join(' ') || address.Line1 || address.Label || '';

          const community = address.City || address.District || '';
          const postalCode = address.PostalCode || '';
          const label = address.Label || [addressLine1, community, provinceCode, postalCode].filter(Boolean).join(', ');

          setAddressLookupError('');
          setAddressInput(label);
          setForm((current) => ({
            ...current,
            addressLine1,
            community,
            postalCode,
          }));
        });
      } catch (lookupError) {
        console.error(lookupError);
        if (!cancelled) {
          setAddressLookupError('Address suggestions are temporarily unavailable. You can still enter your address manually.');
        }
      }
    };

    initializeAddressComplete();

    return () => {
      cancelled = true;
    };
  }, [activeStep]);

  const queuePayload = useMemo(
    () => buildQueuePayload(form, confirmationNumber || 'NFPR', 'Need a Family Practice Registry Intake'),
    [form, confirmationNumber]
  );

  const healthFlags = queuePayload.metadata.healthNeedsFlags;
  const progress = ((activeStep + 1) / registerSteps.length) * 100;
  const shouldRouteTo811 = form.livesInNovaScotia === 'no' || form.hasHealthCard === 'no';
  const canContinueEligibility = form.livesInNovaScotia && form.hasProvider && form.hasHealthCard;
  const registerHealthCardInvalid = Boolean(form.healthCardNumber) && !isValidNSHealthCardNumber(form.healthCardNumber);
  const confirmHealthCardInvalid = Boolean(form.confirmHealthCardNumber) && !isValidNSHealthCardNumber(form.confirmHealthCardNumber);
  const removeHealthCardInvalid = Boolean(form.removeHealthCardNumber) && !isValidNSHealthCardNumber(form.removeHealthCardNumber);
  const canSubmitRegister = Boolean(
    form.firstName &&
    form.lastName &&
    form.dateOfBirth &&
    isValidNSHealthCardNumber(form.healthCardNumber) &&
    form.phone &&
    form.addressLine1 &&
    form.community &&
    form.postalCode &&
    form.consentAccuracy &&
    form.consentContact &&
    form.consentEmergency
  );

  const updateField = (field) => (event) => {
    const rawValue = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    let value = rawValue;

    if (field === 'postalCode') {
      value = rawValue.toUpperCase();
    }

    if (healthCardFields.has(field)) {
      value = formatNSHealthCardNumber(rawValue);
    }

    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleAddressSelection = async (selectedAddress) => {
    if (!selectedAddress || typeof selectedAddress === 'string') {
      return;
    }

    setAddressLookupError('');

    if (selectedAddress.source === 'canada-post-addresscomplete') {
      try {
        if (selectedAddress.next === 'Find') {
          const nestedSuggestions = await findAddressCompleteSuggestions(addressInput, selectedAddress.id);
          setAddressOptions(nestedSuggestions);
          return;
        }

        const retrievedAddress = await retrieveAddressCompleteAddress(selectedAddress.id);
        if (!retrievedAddress) {
          setAddressLookupError('We could not retrieve that address. You can still enter your address manually.');
          return;
        }

        if (retrievedAddress.provinceCode && retrievedAddress.provinceCode.toUpperCase() !== 'NS') {
          setAddressLookupError('Please select a Nova Scotia address or enter your Nova Scotia address manually.');
          return;
        }

        setAddressInput(retrievedAddress.label);
        setForm((current) => ({
          ...current,
          addressLine1: retrievedAddress.addressLine1,
          community: retrievedAddress.community,
          postalCode: retrievedAddress.postalCode,
        }));
        return;
      } catch (lookupError) {
        console.error(lookupError);
        setAddressLookupError('Address suggestions are temporarily unavailable. You can still enter your address manually.');
        return;
      }
    }

    setAddressInput(selectedAddress.label);
    setForm((current) => ({
      ...current,
      addressLine1: selectedAddress.addressLine1,
      community: selectedAddress.community,
      postalCode: selectedAddress.postalCode,
      county: selectedAddress.county || current.county,
    }));
  };

  const resetAll = () => {
    setForm(initialForm);
    setServiceAction('');
    setActiveStep(0);
    setSubmitted(false);
    setSubmittedAction('');
    setConfirmResult(null);
    setConfirmationNumber('');
    setError('');
    setAddressInput('');
    setAddressOptions(localAddressSuggestions.slice(0, 8));
    setAddressLoading(false);
    setAddressLookupError('');
  };

  const next = () => setActiveStep((current) => Math.min(current + 1, registerSteps.length - 1));
  const back = () => setActiveStep((current) => Math.max(current - 1, 0));

  const saveSubmission = async (payload) => {
    await axios.post(`${apiUrl}family-practice-registry`, payload);
  };

  const submitRegister = async () => {
    setError('');
    const confirmation = createConfirmationNumber('NFPR');
    const payload = buildQueuePayload(form, confirmation, 'Need a Family Practice Registry Intake');
    console.log('payload', payload);

    try {
      await saveSubmission(payload);
      setConfirmationNumber(confirmation);
      setSubmittedAction('register');
      setSubmitted(true);
    } catch (submitError) {
      console.error(submitError);
      setError('We could not submit your request. Please try again or call 811 for help.');
    }
  };

  const submitServiceRequest = async (type) => {
    setError('');
    const payload = {
      organization: 'Nova Scotia 811',
      patient: {
        lastName: type === 'confirm' ? form.confirmLastName : form.removeLastName,
        dateOfBirth: type === 'confirm' ? form.confirmDateOfBirth : form.removeDateOfBirth,
        healthCardNumber: type === 'confirm' ? form.confirmHealthCardNumber : form.removeHealthCardNumber,
      },
    };

    try {
      const url = `${apiUrl}family-practice-registry/${type === 'confirm' ? 'confirm' : 'remove'}`;
      const response = await axios.post(url, payload, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });
      const data = response?.data?.data || response?.data || {};

      if (type === 'confirm') {
        setConfirmResult({
          found: Boolean(data.found),
          status: data.status || '',
          confirmationNumber: data.confirmationNumber || '',
        });
        setConfirmationNumber(data.confirmationNumber || '');
        setSubmittedAction(type);
        setSubmitted(true);
        return;
      }

      // remove
      if (!data.removed) {
        setError('We could not find a registry request to remove matching that information. Please double-check your details or call 811 for help.');
        return;
      }
      setConfirmationNumber(data.confirmationNumber || '');
      setSubmittedAction(type);
      setSubmitted(true);
    } catch (submitError) {
      console.error(submitError);
      setError('We could not submit your request. Please try again or call 811 for help.');
    }
  };

  const confirmationTitle = {
    register: 'Your registry request has been submitted',
    confirm: confirmResult?.found
      ? 'We found your registry request'
      : 'No registry request found',
    remove: 'Your removal request has been submitted',
  }[submittedAction] || 'Your request has been submitted';

  if (submitted) {
    if (submittedAction === 'confirm' && confirmResult) {
      const statusLabel = getRegistryStatusLabel(confirmResult.status);

      return (
        <Box className="nfpr-page">
          <Container maxWidth="lg">
            <Paper elevation={0} className="nfpr-confirmation-card">
              <LogoHeader centered />
              {confirmResult.found ? (
                <CheckCircleIcon className="nfpr-confirmation-icon" />
              ) : (
                <SearchOffIcon className="nfpr-confirmation-icon" color="warning" />
              )}
              <Typography variant="h3" component="h1">{confirmationTitle}</Typography>

              {confirmResult.found ? (
                <>
                  <Typography className="nfpr-muted" variant="h6">
                    Confirmation number: <strong>{confirmResult.confirmationNumber}</strong>
                  </Typography>
                  <Alert severity="success" className="nfpr-alert">
                    We found an active registry request matching the information you provided.
                  </Alert>

                  <Grid container spacing={3} className="nfpr-confirmation-grid">
                    <Grid item xs={12} md={6}>
                      <Card elevation={0} className="nfpr-card nfpr-next-card">
                        <CardContent>
                          <Typography variant="h5">Current status</Typography>
                          <Chip label={statusLabel} className="nfpr-status-chip" />
                          <Typography>
                            This is the current status of your registry request. Call 811 if you have questions about what it means or what happens next.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card elevation={0} className="nfpr-card nfpr-next-card">
                        <CardContent>
                          <Typography variant="h5">Need to make a change?</Typography>
                          <Typography>
                            Call 811 and provide your confirmation number if your address, phone number, family information, or care needs change.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <>
                  <Alert severity="warning" className="nfpr-alert">
                    We could not find an active registry request matching that information. Please double-check your last name, date of birth, and health card number, or call 811 for help.
                  </Alert>

                  <Grid container spacing={3} className="nfpr-confirmation-grid">
                    <Grid item xs={12} md={6}>
                      <Card elevation={0} className="nfpr-card nfpr-next-card">
                        <CardContent>
                          <Typography variant="h5">What you can do next</Typography>
                          <Typography>
                            If you have not registered yet, choose Register or update from the registry options. If you believe this is an error, call 811 for assistance.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card elevation={0} className="nfpr-card nfpr-next-card">
                        <CardContent>
                          <Typography variant="h5">Check your details</Typography>
                          <Typography>
                            Make sure the last name, date of birth, and Nova Scotia health card number match what was used when you registered.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </>
              )}

              <Alert severity="info" className="nfpr-alert" icon={<InfoOutlinedIcon />}>
                This service is not for emergencies or urgent medical care. If you need urgent medical help, call 911 or go to the nearest emergency department.
              </Alert>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" className="nfpr-actions">
                {confirmResult.found ? (
                  <Button variant="contained" onClick={() => window.print()}>Print confirmation</Button>
                ) : null}
                <Button variant={confirmResult.found ? 'outlined' : 'contained'} onClick={resetAll}>Return to registry options</Button>
              </Stack>
            </Paper>
          </Container>
        </Box>
      );
    }

    return (
      <Box className="nfpr-page">
        <Container maxWidth="lg">
          <Paper elevation={0} className="nfpr-confirmation-card">
            <LogoHeader centered />
            <CheckCircleIcon className="nfpr-confirmation-icon" />
            <Typography variant="h3" component="h1">{confirmationTitle}</Typography>
            <Typography className="nfpr-muted" variant="h6">
              Confirmation number: <strong>{confirmationNumber}</strong>
            </Typography>
            <Alert severity="success" className="nfpr-alert">
              Please save this confirmation number. You can use it if you need to contact 811 about this request.
            </Alert>

            <Grid container spacing={3} className="nfpr-confirmation-grid">
              <Grid item xs={12} md={4}>
                <Card elevation={0} className="nfpr-card nfpr-next-card">
                  <CardContent>
                    <Typography variant="h5">Current status</Typography>
                    <Chip label="Submitted" className="nfpr-status-chip" />
                    <Typography>
                      Your request has been received. Keep your confirmation number for your records.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={0} className="nfpr-card nfpr-next-card">
                  <CardContent>
                    <Typography variant="h5">What happens next</Typography>
                    <Typography>
                      Registry support staff may review your information and contact you if more details are needed. Please keep your contact information up to date.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={0} className="nfpr-card nfpr-next-card">
                  <CardContent>
                    <Typography variant="h5">Need to make a change?</Typography>
                    <Typography>
                      Call 811 and provide your confirmation number if your address, phone number, family information, or care needs change.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Alert severity="info" className="nfpr-alert">
              This service is not for emergencies or urgent medical care. If you need urgent medical help, call 911 or go to the nearest emergency department.
            </Alert>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" className="nfpr-actions">
              <Button variant="contained" onClick={() => window.print()}>Print confirmation</Button>
              <Button variant="outlined" onClick={resetAll}>Return to registry options</Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="nfpr-page">
      <Container maxWidth="lg">
        <Paper elevation={0} className="nfpr-hero">
          <Box className="nfpr-hero-content">
            <LogoHeader />
            <Chip label="Primary care access" className="nfpr-service-chip" />
            <Typography variant="h2" component="h1">Need a Family Practice Registry</Typography>
            <Typography variant="h6" className="nfpr-hero-subtitle">
              Join the registry, update your information, confirm your registration, or remove your request using a guided, accessible online service.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} className="nfpr-hero-highlights" aria-label="Service highlights">
              <Chip label="Register or update" />
              <Chip label="Confirm registration" />
              <Chip label="Remove request" />
            </Stack>
          </Box>
          <Box className="nfpr-hero-aside" aria-label="811 support information">
            <Box className="nfpr-hero-aside-icon" aria-hidden="true">
              <LocalHospitalIcon />
            </Box>
            <Typography variant="overline">811 support</Typography>
            <Typography variant="h5">Need help?</Typography>
            <Typography variant="body2">
              If you are new to Nova Scotia and do not have a Nova Scotia Health Card, call 811 for help registering.
            </Typography>
          </Box>
        </Paper>

        {!serviceAction && (
          <>
            <Grid container spacing={3} className="nfpr-action-grid">
              <Grid item xs={12} md={4}>
                <Card elevation={0} className="nfpr-card nfpr-action-card">
                  <CardActionArea onClick={() => setServiceAction('register')} aria-label="Register or update your information">
                    <CardContent>
                      <AssignmentTurnedInIcon className="nfpr-action-icon" />
                      <Typography variant="h5">Register or update</Typography>
                      <Typography className="nfpr-muted">
                        Join the registry or update your contact, family, or health needs information.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={0} className="nfpr-card nfpr-action-card">
                  <CardActionArea onClick={() => setServiceAction('confirm')} aria-label="Confirm your registration">
                    <CardContent>
                      <ManageSearchIcon className="nfpr-action-icon" />
                      <Typography variant="h5">Confirm registration</Typography>
                      <Typography className="nfpr-muted">
                        Check that your registry request is still active and your details are current.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card elevation={0} className="nfpr-card nfpr-action-card">
                  <CardActionArea onClick={() => setServiceAction('remove')} aria-label="Remove yourself from the registry">
                    <CardContent>
                      <PersonRemoveIcon className="nfpr-action-icon" />
                      <Typography variant="h5">Remove request</Typography>
                      <Typography className="nfpr-muted">
                        Remove your request if you no longer need help finding a family doctor or nurse practitioner.
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card elevation={0} className="nfpr-card">
                  <CardContent>
                    <Typography variant="h5">Who should use this registry?</Typography>
                    <Typography>
                      This registry supports Nova Scotians who are looking for a family doctor or nurse practitioner, including people who do not have a provider or whose provider is moving, retiring, or closing their practice.
                    </Typography>
                    <Alert severity="info" className="nfpr-side-alert">
                      New to Nova Scotia and do not have a Nova Scotia Health Card? Please call 811 for help registering.
                    </Alert>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={5}>
                <Card elevation={0} className="nfpr-card">
                  <CardContent>
                    <Typography variant="h5">Privacy and participation</Typography>
                    <Typography>
                      Participation is voluntary. The information you provide is used to support your registry request and help identify services that may match your needs.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}

        {serviceAction === 'register' && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card elevation={0} className="nfpr-card">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" className="nfpr-step-header">
                    <Typography variant="h5">{registerSteps[activeStep]}</Typography>
                    <Typography variant="body2" className="nfpr-muted">Step {activeStep + 1} of {registerSteps.length}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={progress} className="nfpr-progress" aria-label={`Step ${activeStep + 1} of ${registerSteps.length}`} />

                  {activeStep === 0 && (
                    <Stack spacing={3}>
                      <Alert severity="info">
                        If you do not have a Nova Scotia Health Card, please call 811 for assisted registration.
                      </Alert>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField select required fullWidth label="Do you live in Nova Scotia?" value={form.livesInNovaScotia} onChange={updateField('livesInNovaScotia')}>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField select required fullWidth label="Do you have a family doctor or nurse practitioner?" value={form.hasProvider} onChange={updateField('hasProvider')}>
                            <MenuItem value="no">No</MenuItem>
                            <MenuItem value="yes-provider-moving">My provider is moving, retiring, or closing their practice</MenuItem>
                            <MenuItem value="yes">Yes</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField select required fullWidth label="Do you have a Nova Scotia Health Card?" value={form.hasHealthCard} onChange={updateField('hasHealthCard')}>
                            <MenuItem value="yes">Yes</MenuItem>
                            <MenuItem value="no">No</MenuItem>
                          </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField select fullWidth label="Who are you registering?" value={form.registeringFor} onChange={updateField('registeringFor')}>
                            <MenuItem value="self">Myself</MenuItem>
                            <MenuItem value="family">Myself and family members</MenuItem>
                            <MenuItem value="someoneElse">Someone else</MenuItem>
                          </TextField>
                        </Grid>
                      </Grid>
                      {shouldRouteTo811 && (
                        <Alert severity="warning">
                          Based on your answers, please call 811 for help with registration. You can still review the information on this page, but phone support may be required to complete your request.
                        </Alert>
                      )}
                    </Stack>
                  )}

                  {activeStep === 1 && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}><TextField required fullWidth label="First name" value={form.firstName} onChange={updateField('firstName')} /></Grid>
                      <Grid item xs={12} sm={6}><TextField required fullWidth label="Last name" value={form.lastName} onChange={updateField('lastName')} /></Grid>
                      <Grid item xs={12} sm={6}><TextField required fullWidth label="Date of birth" type="date" InputLabelProps={{ shrink: true }} value={form.dateOfBirth} onChange={updateField('dateOfBirth')} /></Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="NS Health Card Number"
                          value={form.healthCardNumber}
                          onChange={updateField('healthCardNumber')}
                          error={registerHealthCardInvalid}
                          helperText={getHealthCardHelperText(form.healthCardNumber)}
                          inputProps={{ inputMode: 'numeric', 'aria-describedby': 'nfpr-health-card-helper' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}><TextField required fullWidth label="Phone" value={form.phone} onChange={updateField('phone')} /></Grid>
                      <Grid item xs={12} sm={6}><TextField fullWidth label="Email" value={form.email} onChange={updateField('email')} /></Grid>
                      <Grid item xs={12}>
                        <TextField
                          required
                          fullWidth
                          id="nfpr-address-search"
                          label="Street address"
                          value={addressInput || form.addressLine1}
                          onChange={(event) => {
                            const value = event.target.value;
                            setAddressInput(value);
                            setForm((current) => ({ ...current, addressLine1: value }));
                          }}
                          helperText={addressLookupError || 'Start typing your address, then select a match or continue typing manually.'}
                          error={Boolean(addressLookupError)}
                          inputProps={{
                            'aria-label': 'Street address. Start typing to see address suggestions.',
                            autoComplete: 'off',
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Community"
                          value={form.community}
                          onChange={updateField('community')}
                          helperText="This may fill automatically when you select an address."
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          label="Postal code"
                          value={form.postalCode}
                          onChange={updateField('postalCode')}
                          inputProps={{ maxLength: 7 }}
                          helperText="Example: B3J 3K5"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth select label="Preferred language" value={form.preferredLanguage} onChange={updateField('preferredLanguage')}>
                          {languages.map((language) => <MenuItem key={language} value={language}>{language}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth select label="Preferred contact method" value={form.preferredContactMethod} onChange={updateField('preferredContactMethod')}>
                          {contactMethods.map((method) => <MenuItem key={method} value={method}>{method}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField fullWidth label="Family members included" type="number" inputProps={{ min: 0 }} value={form.familyMemberCount} onChange={updateField('familyMemberCount')} />
                      </Grid>
                    </Grid>
                  )}

                  {activeStep === 2 && (
                    <Stack spacing={2}>
                      <Typography className="nfpr-muted">These questions help registry support understand your needs. Select all that apply.</Typography>
                      <Grid container spacing={1}>
                        {[
                          ['medicationNeeds', 'Ongoing medication needs'],
                          ['chronicCondition', 'Chronic condition'],
                          ['pregnant', 'Pregnant'],
                          ['mobilityNeeds', 'Mobility or accessibility needs'],
                          ['mentalHealthSupport', 'Mental health or addictions support'],
                          ['transportationBarrier', 'Difficulty travelling for care'],
                          ['virtualCareSupport', 'Needs help accessing virtual care'],
                        ].map(([field, label]) => (
                          <Grid item xs={12} sm={6} key={field}>
                            <FormControlLabel control={<Checkbox checked={Boolean(form[field])} onChange={updateField(field)} />} label={label} />
                          </Grid>
                        ))}
                      </Grid>
                      <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        label="Anything else you would like registry support to know?"
                        value={form.additionalNotes}
                        onChange={updateField('additionalNotes')}
                      />
                    </Stack>
                  )}

                  {activeStep === 3 && (
                    <Stack spacing={3}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><Field label="Applicant" value={`${form.firstName} ${form.lastName}`.trim()} /></Grid>
                        <Grid item xs={12} sm={6}><Field label="Address" value={`${form.addressLine1}${form.community ? `, ${form.community}` : ''}${form.postalCode ? `, ${form.postalCode}` : ''}`} /></Grid>
                        <Grid item xs={12} sm={6}><Field label="Preferred contact" value={form.preferredContactMethod} /></Grid>
                        <Grid item xs={12} sm={6}><Field label="Family members included" value={form.familyMemberCount} /></Grid>
                        <Grid item xs={12}>
                          <Box className="nfpr-summary-field">
                            <Typography variant="caption">Health needs selected</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {healthFlags.length ? healthFlags.map((flag) => <Chip key={flag} label={flag} />) : <Chip label="None selected" />}
                            </Stack>
                          </Box>
                        </Grid>
                      </Grid>
                      <Divider />
                      <Stack spacing={1}>
                        <FormControlLabel control={<Checkbox checked={form.consentAccuracy} onChange={updateField('consentAccuracy')} />} label="I confirm the information provided is accurate to the best of my knowledge." />
                        <FormControlLabel control={<Checkbox checked={form.consentContact} onChange={updateField('consentContact')} />} label="I consent to be contacted by 811 or registry support staff about this request." />
                        <FormControlLabel control={<Checkbox checked={form.consentEmergency} onChange={updateField('consentEmergency')} />} label="I understand this service is not for emergencies or urgent medical care." />
                      </Stack>
                      {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" className="nfpr-actions" spacing={2}>
                    <Button startIcon={<ArrowBackIcon />} onClick={activeStep === 0 ? resetAll : back}>Back</Button>
                    {activeStep < registerSteps.length - 1 ? (
                      <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={next} disabled={activeStep === 0 && !canContinueEligibility}>Continue</Button>
                    ) : (
                      <Button
                        variant="contained"
                        disabled={!canSubmitRegister}
                        onClick={submitRegister}
                      >
                        Submit request
                      </Button>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={0} className="nfpr-card nfpr-side-card">
                <CardContent>
                  <Typography variant="h5">About this request</Typography>
                  <Typography className="nfpr-muted">
                    Complete the form as accurately as possible. Registry support may contact you if more information is needed.
                  </Typography>
                  <Divider className="nfpr-divider" />
                  <Field label="Current step" value={registerSteps[activeStep]} />
                  <Field label="After you submit" value="You will receive a confirmation number" />
                  <Alert severity="info" className="nfpr-side-alert">
                    Do not use this service for emergencies. Call 911 or go to the nearest emergency department if you need urgent medical help.
                  </Alert>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {serviceAction === 'confirm' && (
          <Card elevation={0} className="nfpr-card nfpr-single-form">
            <CardContent>
              <Typography variant="h4" component="h2">Confirm your registration</Typography>
              <Typography className="nfpr-muted">
                Enter the information below to request confirmation that your registry request is active.
              </Typography>
              <Grid container spacing={2} className="nfpr-form-grid">
                <Grid item xs={12} md={4}><TextField required fullWidth label="Last name" value={form.confirmLastName} onChange={updateField('confirmLastName')} /></Grid>
                <Grid item xs={12} md={4}><TextField required fullWidth label="Date of birth" type="date" InputLabelProps={{ shrink: true }} value={form.confirmDateOfBirth} onChange={updateField('confirmDateOfBirth')} /></Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="NS Health Card Number"
                    value={form.confirmHealthCardNumber}
                    onChange={updateField('confirmHealthCardNumber')}
                    error={confirmHealthCardInvalid}
                    helperText={getHealthCardHelperText(form.confirmHealthCardNumber)}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
              </Grid>
              {error && <Alert severity="error" className="nfpr-side-alert">{error}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} className="nfpr-actions">
                <Button startIcon={<ArrowBackIcon />} onClick={resetAll}>Back</Button>
                <Button variant="contained" onClick={() => submitServiceRequest('confirm')} disabled={!(form.confirmLastName && form.confirmDateOfBirth && isValidNSHealthCardNumber(form.confirmHealthCardNumber))}>Submit request</Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {serviceAction === 'remove' && (
          <Card elevation={0} className="nfpr-card nfpr-single-form">
            <CardContent>
              <Typography variant="h4" component="h2">Remove your registry request</Typography>
              <Typography className="nfpr-muted">
                Use this option if you no longer need to be on the registry, such as if you have found a family doctor or nurse practitioner.
              </Typography>
              <Grid container spacing={2} className="nfpr-form-grid">
                <Grid item xs={12} md={4}><TextField required fullWidth label="Last name" value={form.removeLastName} onChange={updateField('removeLastName')} /></Grid>
                <Grid item xs={12} md={4}><TextField required fullWidth label="Date of birth" type="date" InputLabelProps={{ shrink: true }} value={form.removeDateOfBirth} onChange={updateField('removeDateOfBirth')} /></Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="NS Health Card Number"
                    value={form.removeHealthCardNumber}
                    onChange={updateField('removeHealthCardNumber')}
                    error={removeHealthCardInvalid}
                    helperText={getHealthCardHelperText(form.removeHealthCardNumber)}
                    inputProps={{ inputMode: 'numeric' }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Checkbox checked={form.removeConsent} onChange={updateField('removeConsent')} />} label="I confirm that I want to remove this request from the Need a Family Practice Registry." />
                </Grid>
              </Grid>
              {error && <Alert severity="error" className="nfpr-side-alert">{error}</Alert>}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} className="nfpr-actions">
                <Button startIcon={<ArrowBackIcon />} onClick={resetAll}>Back</Button>
                <Button variant="contained" onClick={() => submitServiceRequest('remove')} disabled={!(form.removeLastName && form.removeDateOfBirth && isValidNSHealthCardNumber(form.removeHealthCardNumber) && form.removeConsent)}>Submit removal request</Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        <PoweredByFooter />
      </Container>
    </Box>
  );
};

export default NFPRPublicIntake;
